<template>
<div class="px-1 pt-1">
  <div v-if="error" class="alert alert-danger mt-2">
    {{ error }}
  </div>
  <div v-else-if="error2" class="alert alert-danger mt-2">
    {{ error2 }}
  </div>
  <VueApexCharts v-else-if="stats"
                 width="100%"
                 height="300px"
                 :options="chartOptions"
                 :series="series"
                 @zoomed="zoomHandler"
                 @scrolled="scrollHandler"
                 @dataPointSelection="selectionHandler"
  />
  <StockList :filtered-list="filteredList" />
</div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { storeToRefs } from 'pinia';
import VueApexCharts from 'vue3-apexcharts'
import { ApexOptions } from "apexcharts";
import { useBusinessIntelligenceStore } from "@/stores/businessIntelligence.store.ts";
import { useStockStore } from "@/stores";
import StockList from "@/components/lists/StockList.vue";
import {useI18n} from "vue-i18n";
const i18n = useI18n();
  
const statisticsStore = useBusinessIntelligenceStore();
const { expiryDates: stats, error } = storeToRefs(statisticsStore);
const stockStore = useStockStore();
const { data: stock, error: error2 } = storeToRefs(stockStore);
  
const minExpiryDate = ref<Date | undefined>();
const maxExpiryDate = ref<Date | undefined>();

const series = computed(() => {
  // we insert this to make the view look nicer in case the next stock is far away from today
  const minDate = [{ x: new Date().toDateString(), y: 0 }];
  return stats.value?.map(x => ({ name: x.site.name, data: minDate.concat(x.values) }))
});

const filteredList = computed(() =>
  stock.value
    .filter(x => x.expiryAsDate !== undefined
      && (!minExpiryDate.value || minExpiryDate.value <= x.expiryAsDate)
      && (!maxExpiryDate.value || maxExpiryDate.value >= x.expiryAsDate))
    .sort((x, y) => x.expiryAsDate!.getTime() - y.expiryAsDate!.getTime())
);

onMounted(async () => {
  await refresh();
});

async function refresh() {
  error.value = "";
  error2.value = "";

  await stockStore.fetchAll();
  await statisticsStore.fetchExpiryDates();
}
  
function selectionHandler(_: any, _1: any, config: any) {
  const selected = series.value![config.seriesIndex]!.data[config.dataPointIndex]!.x
  minExpiryDate.value = new Date(selected)
  maxExpiryDate.value = new Date(selected)
}

function zoomHandler(_: any, { xaxis }: any) {
  minExpiryDate.value = new Date(xaxis.min);
  maxExpiryDate.value = new Date(xaxis.max);
}

function scrollHandler(_: any, { xaxis }: any) {
  minExpiryDate.value = new Date(xaxis.min);
  maxExpiryDate.value = new Date(xaxis.max);
}

const chartOptions = computed<ApexOptions>(() => {
  return {
    chart: {
      id: "Expiry Dates",
      type: 'bar',
      stacked: true,
    },
    plotOptions: {
      bar: {
        columnWidth: "500%"
      },
    },
    dataLabels: {
      enabled: false
    },
    fill: {
      colors: ['#3C618C', '#979C4B']
    },
    markers: {
      size: 5,
      colors: ['red']
    },
    states: {
      normal: {
        filter: { type: 'none', value: 0 },
      },
      hover: {
        filter: { type: 'lighten', value: 0.2 },
      },
      active: {
        filter: { type: 'darken', value: 0.4 },
        allowMultipleDataPointsSelection: false,
      },
    },
    xaxis: {
      type: 'datetime'
    },
    yaxis: {
      min: 0,
      stepSize: 1,
      show: true
    },
    annotations: {
      xaxis: [
        {
          x: new Date().getTime(),
          borderColor: "#FEB019",
          label: {
            borderColor: "#FEB019",
            style: {
              color: "#fff",
              background: "#FEB019"
            },
            orientation: 'horizontal',
            text: i18n.t('page.statistics.labels.today')
          }
        }
      ]
    }
  }
});
</script>
